import { store, router } from '@/appContext';
import Analytics from '@/mixins/Analytics';
import {
  ApolloLink, // apollo links must return observables
  HttpLink,
  split,
  from,
  Observable,
} from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { decodeJwt } from '@/utilities';
import { isLocked } from '@/utilities/JwtRefreshTokensLock';
import { getDifferenceInMinutes } from '@/utilities/DateUtils';

// Headers to be passed to HTTP and WS requests
let startingHeaders = {};

const setStartingHeaders = (additionalHeaders = {}) => {
  // Pass json-naming-strategy header as camel or snake to change response keys (default is camel)

  startingHeaders = {
    ...additionalHeaders,
    'acv-client-version': import.meta.env.VITE_VERSION,
    'acv-client-type': 'desktop',
    'acv-client-os': 'web',
    'acv-client-timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
    'Content-Type': 'application/json',
  };
};

const getHeaders = async () => {
  const { jwtToken } = await store.dispatch('getJwtTokens');

  return {
    ...startingHeaders,
    'acv-user-id': store.getters.currentUser.id,
    Authorization: `Bearer ${jwtToken}`,
  };
};

// Helper to set authentication headers
const setAuthHeaders = async (operation) => {
  const headersToUse = await getHeaders();
  operation.setContext(({ headers }) => ({
    headers: {
      ...headersToUse,
      ...headers,
    },
  }));
};

// Helper to handle token refresh logic if close to expiration (should match axios interceptors)
const handleTokenRefresh = async () => {
  const {
    lastUpdated,
    jwtToken,
    refreshToken,
    lastRefreshFailed = false,
  } = await store.dispatch('getJwtTokens');

  const decodedJwtToken = decodeJwt(jwtToken) ?? '';
  const jwtExpirationVal = new Date(0);
  jwtExpirationVal.setUTCSeconds(decodedJwtToken?.exp ?? 0);

  const canRefreshTokens = !!(jwtToken && refreshToken && lastUpdated);

  if (canRefreshTokens && !lastRefreshFailed) {
    const refreshIsLocked = isLocked();
    const currentDate = new Date();

    const jwtDifferenceInMinutes = getDifferenceInMinutes(
      jwtExpirationVal,
      currentDate,
    );

    const luDifferenceInMinutes = getDifferenceInMinutes(
      currentDate,
      lastUpdated,
    );

    const atLeast12MinsSinceLastUpdate = luDifferenceInMinutes >= 12;
    const tokensCloseToExpiration = jwtDifferenceInMinutes < 3
      || atLeast12MinsSinceLastUpdate;

    if (!refreshIsLocked && tokensCloseToExpiration) {
      await store.dispatch('refreshJwtTokens');
    } else if (refreshIsLocked && tokensCloseToExpiration) {
      await store.dispatch('waitForOtherWindowToRefreshJwtToken');
    }
  }
};

// Idle Link - only process query while not idle
const idleLink = () => new ApolloLink((operation, forward) => {
  const { isIdle } = store.getters;
  if (!isIdle) {
    return forward(operation);
  }

  // cancel the request while idle
  return new Observable((observer) => {
    observer.next({
      data: null,
      loading: false,
      networkStatus: 8,
    });
    observer.complete();
  });
});

// Refresh Token Link - ensures tokens are up to date, handle as an observable for the promise
const refreshTokenLink = () => new ApolloLink((operation, forward) => new Observable(
  (observer) => {
    handleTokenRefresh()
      .then(() => {
        const subscription = forward(operation).subscribe(observer);
        return subscription;
      })
      .catch((error) => observer.error(error));
  },
));

// Authentication Link - set headers for authentication, handle as an observable for the promise
const authenticationLink = () => (
  new ApolloLink((operation, forward) => new Observable(
    (observer) => {
      setAuthHeaders(operation)
        .then(() => {
          const subscription = forward(operation).subscribe(observer);
          return subscription;
        })
        .catch((error) => observer.error(error));
    },
  ))
);

// After Response Link - Handle Transform the response properties if necessary
const afterResponseLink = () => new ApolloLink((operation, forward) => forward(operation));

// Error Link (Terminal) - Log network or graphql errors, redirect on 401
const onErrorLink = () => onError(({ graphQLErrors, networkError }) => {
  if (networkError?.statusCode === 401) {
    Analytics.methods.noticeError('GraphQL nauthorized network error', { networkError });
    router.push('/logout');
  }

  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      Analytics.methods.noticeError('GraphQL definition error', { error });
    });
  }
});

// Connection to Websocket Setup for subscriptions - Terminal Link
const setupSubscriptionLink = (wsUri = '') => new GraphQLWsLink(
  createClient({
    url: wsUri,
    reconnect: true,
    connectionParams: async () => {
      const headersToUse = await getHeaders();

      return {
        ...headersToUse,
      };
    },
  }),
);

// Connection to HTTP Query for queries and mutations - Terminal Link
const setupHttpLink = (httpUri = '') => new HttpLink({ uri: httpUri });

// Create link combination for ApolloClient
const getCombinedLink = (baseDomain = '', additionalHeaders = {}) => {
  if (!baseDomain) {
    throw new Error('A base domain is required');
  }

  setStartingHeaders(additionalHeaders);

  const domainLinks = {
    http: `https://${baseDomain}`,
    // ws: `wss://${baseDomain}`,
    ws: 'wss://gaia.internal.latest.acvauctions.com/query', // temporary
  };

  // additively include the links to the chain (order matters)
  const combinedLink = [
    idleLink(),
    refreshTokenLink(),
    authenticationLink(),
    afterResponseLink(),
    onErrorLink(),
  ];

  // Splits the requests to point to websockets or http by query with the combinedLink
  const link = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition'
        && definition.operation === 'subscription'
      );
    },
    from([...combinedLink, setupSubscriptionLink(domainLinks.ws)]),
    from([...combinedLink, setupHttpLink(domainLinks.http)]),
  );

  return link;
};

export { handleTokenRefresh, getHeaders };

export default getCombinedLink;
